import { createFileRoute, redirect } from '@tanstack/react-router';

import { queryClient } from '~/api/queryClient';
import { queryKeys } from '~/api/queryKeys';
import { getOnboardingStatus } from '~/features/onboarding/services/onboarding-status.service';
import {
  resolveOnboardingRedirect,
  toRedirectArgs,
  type OnboardingRedirect,
} from '~/features/onboarding/utils/resolve-onboarding-redirect';

/**
 * Post-login landing route (`/home`). Signed-in visitors to `/` are sent here by the
 * public homepage route (`routes/index.tsx`), which must own `/` so Google's OAuth
 * reviewers see a public page instead of a `/login` redirect. Reads `/integration/onboarding/status` and
 * dispatches the user via `resolveOnboardingRedirect`:
 *   - no channel connected         → `/onboarding`
 *   - any channel import COMPLETE  → `/onboarding`
 *   - any channel IN_PROGRESS      → Step 3 with workflowId, on the Google or
 *                                    Meta tree per connector
 *                                    (`/onboarding/complete-import` or
 *                                    `/onboarding/integration/meta/complete-import`)
 * On API failure falls back to `/onboarding` so the user is never stranded.
 */
export const Route = createFileRoute('/_authenticated/_tenant/home')({
  beforeLoad: async () => {
    let next: OnboardingRedirect;
    try {
      const status = await queryClient.ensureQueryData({
        queryKey: queryKeys.onboarding.status(),
        queryFn: getOnboardingStatus,
      });
      next = resolveOnboardingRedirect(status);
    } catch {
      next = { to: '/onboarding' };
    }
    // The redirect-arg construction is in `toRedirectArgs` so it can be
    // unit-tested independently of the route shell. That function uses
    // a discriminant (`'workflowId' in next`) so any future variant of
    // `OnboardingRedirect` that carries a workflowId is automatically
    // routed correctly — no consumer update required here.
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router requires throwing a redirect
    throw redirect(toRedirectArgs(next));
  },
});
